import React from 'react';
import PageLayout from '../components/PageLayout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { Shield, Eye, AlertTriangle, UserX, Lock, Bell, CheckCircle } from 'lucide-react';

const SAFETY_FEATURES = [
  {
    icon: Eye,
    title: 'Privacy by Default',
    desc: 'Your full name is never shown publicly. Community members see only your first name and last initial. Your email address is never visible to other users.',
    badge: 'Active',
    color: '#10B981',
  },
  {
    icon: Shield,
    title: 'AI Content Moderation',
    desc: 'Every post and message is automatically scanned for harmful content, spam, and policy violations before appearing in community feeds.',
    badge: 'Active',
    color: '#1E3A8A',
  },
  {
    icon: AlertTriangle,
    title: 'Community Reporting',
    desc: 'Any user can report a post, profile, or message with a single tap. Reported content is reviewed within 24 hours by our trust & safety team.',
    badge: 'Active',
    color: '#FF4500',
  },
  {
    icon: UserX,
    title: 'Block & Mute',
    desc: 'You can block any user to prevent them from interacting with you, or mute them to hide their content without notification.',
    badge: 'Coming Soon',
    color: '#A855F7',
  },
  {
    icon: Lock,
    title: 'Secure Payments',
    desc: 'All payments are processed by Moyasar, a PCI DSS-compliant payment gateway. We never store card details on our servers.',
    badge: 'Active',
    color: '#10B981',
  },
  {
    icon: Bell,
    title: 'Safety Alerts',
    desc: 'We send immediate notifications if we detect suspicious activity on your account, such as login from an unrecognized device.',
    badge: 'Active',
    color: '#FF4500',
  },
];

export default function Safety() {
  return (
    <PageLayout>
      <SEOHead
        title="Safety & Trust — Fly N Out"
        description="Discover how Fly N Out protects your privacy and keeps the community safe with AI-powered moderation, encrypted data, and a strict privacy-first approach."
        url="/safety"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#111827] to-[#1E3A8A]/70 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#10B981]/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#10B981]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Safety & Trust</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Your safety is our priority. Here's how we protect you and the Fly N Out community.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Safety features grid */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {SAFETY_FEATURES.map(({ icon: Icon, title, desc, badge, color }) => (
            <Card key={title} className="fly-card p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{title}</h3>
                    <Badge className={`text-[10px] ${
                      badge === 'Active' ? 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30' : 'bg-muted text-muted-foreground'
                    }`}>{badge}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Privacy details */}
        <Card className="fly-card p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Eye className="w-5 h-5 text-[#1E3A8A]" /> Your Privacy on Fly N Out
          </h2>
          <div className="space-y-3">
            {[
              { check: true, text: 'Full name is never shown — only first name + last initial' },
              { check: true, text: 'Email address is never visible to other community members' },
              { check: true, text: 'You control whether posts are anonymous or attributed' },
              { check: true, text: 'Profile photos can be a generated avatar (no real photo required)' },
              { check: true, text: 'You can delete your account and all data at any time' },
              { check: false, text: 'Location data is only collected if you explicitly enable geofenced airport features' },
            ].map(({ check, text }) => (
              <div key={text} className="flex items-start gap-2">
                <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${check ? 'text-[#10B981]' : 'text-muted-foreground'}`} />
                <p className="text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Reporting */}
        <Card className="fly-card p-6 mb-6">
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk' }}>How to Report Issues</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Find the Content', desc: 'Tap the \'•••\' menu on any post, profile, or message.' },
              { step: '2', title: 'Select Report', desc: 'Choose the reason for reporting (spam, harassment, false info, etc.)' },
              { step: '3', title: 'We Review', desc: 'Our team reviews every report within 24 hours and takes appropriate action.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center p-4 rounded-xl bg-muted/50">
                <div className="w-8 h-8 rounded-full bg-[#FF4500] text-white text-sm font-bold flex items-center justify-center mx-auto mb-2">{step}</div>
                <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk' }}>{title}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Contact */}
        <div className="text-center p-6 rounded-2xl bg-muted/30 border border-border">
          <h3 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Need Immediate Help?</h3>
          <p className="text-sm text-muted-foreground mb-4">For urgent safety issues, contact our Trust & Safety team directly.</p>
          <a href="mailto:info@thesaudigate.com"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FF4500] text-white text-sm font-medium hover:bg-[#E63F00] transition-colors">
            <Shield className="w-4 h-4" /> info@thesaudigate.com
          </a>
        </div>
      </div>
    </PageLayout>
  );
}
